import React, { useState } from 'react';
import './ContactForm.css';

interface ContactFormProps {
  surveyData: any;
  onClose: () => void;
}

const ContactForm: React.FC<ContactFormProps> = ({ surveyData, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    projectDescription: '',
    additionalInfo: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission (replace with actual API call)
    try {
      // Here you would normally send to your backend/email service
      console.log('Survey Data:', surveyData);
      console.log('Contact Data:', formData);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="survey-overlay">
        <div className="survey-modal">
          <div className="success-content">
            <div className="success-icon">✨</div>
            <h2>Thank You!</h2>
            <p>
              We've received your project details and will get back to you within 24 hours 
              with a personalized proposal.
            </p>
            <div className="summary-preview">
              <h3>Your Project Summary:</h3>
              <div className="summary-item">
                <strong>Type:</strong> {surveyData.projectType}
              </div>
              <div className="summary-item">
                <strong>Timeline:</strong> {surveyData.timeline}
              </div>
              <div className="summary-item">
                <strong>Budget:</strong> {surveyData.budget}
              </div>
              <div className="summary-item">
                <strong>Priorities:</strong> {surveyData.priorities.speed}% Speed, {surveyData.priorities.budget}% Budget, {surveyData.priorities.quality}% Quality
              </div>
            </div>
            <button className="close-success-button" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="survey-overlay">
      <div className="survey-modal contact-form-modal">
        <div className="survey-header">
          <h2>Let's Build Something Amazing</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="contact-form">
          <div className="form-group">
            <label htmlFor="name">Full Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="form-input"
              placeholder="Your full name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="form-input"
              placeholder="your@email.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="company">Company/Organization</label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Your company name (optional)"
            />
          </div>

          <div className="form-group">
            <label htmlFor="projectDescription">Project Description *</label>
            <textarea
              id="projectDescription"
              name="projectDescription"
              value={formData.projectDescription}
              onChange={handleInputChange}
              required
              className="form-textarea"
              placeholder="Tell us about your project idea, goals, and key features you envision..."
              rows={4}
            />
          </div>

          <div className="form-group">
            <label htmlFor="additionalInfo">Additional Information</label>
            <textarea
              id="additionalInfo"
              name="additionalInfo"
              value={formData.additionalInfo}
              onChange={handleInputChange}
              className="form-textarea"
              placeholder="Any specific requirements, concerns, or questions? (optional)"
              rows={3}
            />
          </div>

          <div className="survey-summary">
            <h3>Your Survey Responses:</h3>
            <div className="summary-grid">
              <div className="summary-item">
                <span>Project Type:</span>
                <span>{surveyData.projectType}</span>
              </div>
              <div className="summary-item">
                <span>Timeline:</span>
                <span>{surveyData.timeline}</span>
              </div>
              <div className="summary-item">
                <span>Budget:</span>
                <span>{surveyData.budget}</span>
              </div>
              <div className="summary-item">
                <span>Experience:</span>
                <span>{surveyData.experience}</span>
              </div>
              <div className="priority-summary">
                <span>Priorities:</span>
                <div className="priority-bars">
                  <div className="priority-bar">
                    <span>Speed: {surveyData.priorities.speed}%</span>
                    <div className="bar">
                      <div className="bar-fill" style={{ width: `${surveyData.priorities.speed}%` }}></div>
                    </div>
                  </div>
                  <div className="priority-bar">
                    <span>Budget: {surveyData.priorities.budget}%</span>
                    <div className="bar">
                      <div className="bar-fill" style={{ width: `${surveyData.priorities.budget}%` }}></div>
                    </div>
                  </div>
                  <div className="priority-bar">
                    <span>Quality: {surveyData.priorities.quality}%</span>
                    <div className="bar">
                      <div className="bar-fill" style={{ width: `${surveyData.priorities.quality}%` }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button 
            type="submit" 
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="loading-spinner"></span>
                Sending...
              </>
            ) : (
              'Send Project Details'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;